import { useState } from 'react';
import { Button, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { Feature } from '../../domain/entities/catalog.entity';
import { useFeatures } from '../hooks/use-features';
import { ManageScopesModal } from './ManageScopesModal';
import { Pagination } from '@react-apps/design-system';
import { DeprecatedBadge } from './DeprecatedBadge';
import { RefreshButton } from '@/shared/RefreshButton';
import { ScopeBadgeList } from '@/shared/scopes/ScopeBadge';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

export function FeaturesPanel() {
  const [page, setPage] = usePageQueryParam();
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [scopesTarget, setScopesTarget] = useState<Feature | null>(null);
  const { data, isLoading, refetch, isFetching } = useFeatures(
    page,
    PER_PAGE,
    showDeprecated,
  );

  const columns: ColumnDef<Feature>[] = [
    {
      key: 'name',
      header: 'Nome',
      render: (row) => (
        <span className="flex items-center gap-2">
          {row.name}
          <DeprecatedBadge deprecatedAt={row.deprecatedAt} />
        </span>
      ),
    },
    {
      key: 'key',
      header: 'Key',
      render: (row) => (
        <span className="text-(--color-text-secondary) font-mono text-xs">
          {row.key}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Descrição',
      render: (row) => (
        <span className="text-(--color-text-secondary) text-sm">
          {row.description}
        </span>
      ),
    },
    {
      key: 'scopeIds',
      header: 'Scopes',
      render: (row) => <ScopeBadgeList ids={row.scopeIds} />,
    },
    {
      key: 'actions',
      header: '',
      width: '1%',
      render: (row: Feature) => (
        <Button
          variant="secondary"
          size="sm"
          className="whitespace-nowrap"
          onClick={() => setScopesTarget(row)}
        >
          Gerenciar scopes
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-(--color-text-secondary) text-sm">
          Features do catálogo e os scopes em que ficam disponíveis.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <label className="text-(--color-text-secondary) flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showDeprecated}
              onChange={(e) => {
                setShowDeprecated(e.target.checked);
                setPage(1);
              }}
            />
            Mostrar depreciadas
          </label>
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhuma feature encontrada."
        striped
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="feature"
        onPageChange={setPage}
      />

      {scopesTarget && (
        <ManageScopesModal
          entity="feature"
          entityId={scopesTarget.id}
          entityName={scopesTarget.name}
          initialScopeIds={scopesTarget.scopeIds}
          open
          onClose={() => setScopesTarget(null)}
        />
      )}
    </div>
  );
}
